import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import { CandidateDetail, JobPostingDetail } from '../types';
import { StageSerializer } from '../types/api';
import { EmailFormValues } from './EmailForm';
import SendPracticalTechnicalCase, {
  SendPracticalTechnicalCaseProps,
} from './SendPracticalTechnicalCaseModal';

interface PartialSendPracticalTechnicalCaseModalProps
  extends Omit<SendPracticalTechnicalCaseProps, 'helmetTitle'> {
  candidateIds: CandidateDetail['id'][];
  jobPostingId?: JobPostingDetail['id'];
  stage?: StageSerializer;
  onSuccess?: (data: void, variables: EmailFormValues, context: unknown) => void | Promise<unknown>;
}

const PartialSendPracticalTechnicalCaseModal = ({
  candidateIds,
  onSuccess,
  jobPostingId,
  stage,
  ...props
}: PartialSendPracticalTechnicalCaseModalProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const user = useAPIQuery('/users/me/');

  const mutation = useAPIMutation(
    async (
      { message, attachments, subject, fromName, replyTo, enableReplyTo }: EmailFormValues,
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      for (const candidateId of candidateIds) {
        await api.post('/email-communications/', {
          from_name: fromName,
          candidate: candidateId,
          job_posting: jobPostingId,
          subject,
          message,
          ...(enableReplyTo ? { reply_to: replyTo } : {}),
          type: 'PRACTICAL_TECHNICAL_CASE',
          attachments: attachmentIds,
          created_by: user.data?.id,
        });
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate-history/');
      },
    }
  );

  return (
    <SendPracticalTechnicalCase
      title={
        candidateIds.length > 1 ? (
          <span>
            <Trans>Enviar case prático ou técnico para</Trans>{' '}
            <strong>
              {candidateIds.length} <Trans>candidato(s)</Trans>
            </strong>
          </span>
        ) : (
          <span>
            <Trans>Enviar case prático ou técnico</Trans>
          </span>
        )
      }
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      helmetTitle={
        candidateIds.length > 1
          ? `${t('Enviar case prático ou técnico para')} ${candidateIds.length} ${t('candidatos')}`
          : t('Enviar case prático ou técnico')
      }
      loading={mutation.isLoading}
      jobPostingId={jobPostingId}
      stage={stage}
      {...props}
    />
  );
};

export default PartialSendPracticalTechnicalCaseModal;
